/**
 * Mutate client modal handles creating and editing clients.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { CLIENTS_IMPORT_PAGE } from 'src/constants/pathConsts';
import {
  MutateClientValidatorType,
  useMutateClientForm,
} from 'src/features/crm/clients/MutateClientModal/hooks/useMutateClientForm';
import history from 'src/history';
import { useAppDispatch } from 'src/hooks/useStore';
import { FormModal } from 'src/legacy/components/Modals';
import { addClient, updateClientUser } from 'src/store/clients/actions';
import { Client, Company } from 'src/store/clients/types';
import { alertSnackbar } from 'src/store/ui/actions';

export const MutateClientModal = ({
  initialClientData = {},
  // If present, this is an edit modal. If absent, create.
  client,
  // Only used in the create flow, sets the initial company.
  company,
  onCancel: closeModal,
  onClientCreated,
  open,
}: {
  initialClientData?: {
    cognitoFirstName?: string;
    cognitoLastName?: string;
  };
  client?: Client;
  company?: Company;
  onCancel: () => void;
  onClientCreated: (client: Client) => void;
  open: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const initialFormValue = React.useMemo<MutateClientValidatorType>(() => {
    if (client) {
      return {
        companyId: client.fields.companyId,
        cognitoEmail: client.fields.email,
        cognitoFirstName: client.fields.givenName,
        cognitoLastName: client.fields.familyName,
        userId: client.id,
      };
    }

    return {
      companyId: company?.id ?? '',
      cognitoEmail: '',
      cognitoFirstName: initialClientData.cognitoFirstName ?? '',
      cognitoLastName: initialClientData.cognitoLastName ?? '',
    };
  }, [client, company, initialClientData]);

  const handleSubmitClient = async (data: MutateClientValidatorType) => {
    const isExistingCompany = z
      .string()
      .uuid()
      .safeParse(data.companyId).success;

    const companyParams: { companyId?: string; companyName?: string } = {};

    if (isExistingCompany) {
      // the companyId input was a valid UUID, this means a company
      // was selected from the dropdown (or initial value was used)
      // keep the companyId and set the companyName to undefined
      companyParams.companyId = data.companyId;
      companyParams.companyName = undefined;
    } else if (data.companyId) {
      // the companyId input was a string but not uuid
      // this means a new company was passed in
      companyParams.companyId = undefined;
      companyParams.companyName = data.companyId;
    } else {
      // the companyId input was empty, keep the existing companyId
      companyParams.companyId = client?.fields.companyId;
      companyParams.companyName = undefined;
    }

    if (data.userId) {
      await dispatch(
        updateClientUser({
          ...data,
          ...companyParams,
        }),
      );
      dispatch(alertSnackbar({ successMessage: 'Changes saved' }));
    } else {
      const createdClient = await dispatch(
        addClient({
          ...data,
          ...companyParams,
        }),
      );

      if (createdClient) {
        onClientCreated(createdClient);
      }
    }
  };

  const hasExternalAuthProvider =
    (client?.fields?.externalAuthProviders ?? []).length > 0;

  const additionalModalProps = {
    footerLeftButtonLabel: t(
      ($) => $.page_clients.modals.mutate_client.import_clients,
    ),
    leftButtonProps: {
      className: 'gray-500',
    },
    onFooterLeftButtonClick: () => {
      closeModal();
      history.push(CLIENTS_IMPORT_PAGE.path);
    },
    open,
  };

  return (
    <FormModal<MutateClientValidatorType>
      buttonLabel={t(($) => (client ? $.common.update : $.common.create))}
      initialFormValue={initialFormValue}
      handleSave={handleSubmitClient}
      key="mutate-client-modal"
      modalProps={additionalModalProps}
      onClose={closeModal}
      open={open}
      skipResetOnSubmit
      title={t(($) =>
        client ? $.actions.update_client : $.actions.create_client,
      )}
      useFormHook={() => useMutateClientForm(!!client, hasExternalAuthProvider)}
    />
  );
};
